/**
 * ThresholdStep Component
 *
 * Step 2 of the onboarding wizard - configure alert thresholds.
 * Features:
 * - Critical and warning threshold sliders with auto-adjustment
 * - Healthy status display (no slider)
 * - Selected products badge to navigate back
 *
 * Design Notes:
 * - Matches AlertConfigurationCard from settings
 * - Uses shared threshold colors from constants
 */

import { useState } from 'react';
import {
  BlockStack,
  InlineStack,
  Text,
  Icon,
  Box,
  Tooltip,
  RangeSlider,
  Banner,
  Thumbnail,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  InfoIcon,
  PackageIcon,
} from '@shopify/polaris-icons';
import { THRESHOLD_COLORS, THRESHOLD_TOOLTIPS } from '../../constants/threshold-colors';
import { BRAND_PURPLE } from '../../constants/brand';
import type { WizardProduct } from './types';

/**
 * Internal ThresholdRow component
 * Renders a single threshold configuration row with icon, label + subtext, then slider
 * Layout: Icon | [Label + Subtext] | Slider
 */
function ThresholdRow({
  type,
  icon,
  label,
  tooltipContent,
  slider,
  daysLabel,
}: {
  type: 'critical' | 'warning' | 'healthy';
  icon: typeof AlertTriangleIcon;
  label: string;
  tooltipContent: string;
  slider?: React.ReactNode;
  daysLabel: string;
}) {
  const colors = THRESHOLD_COLORS[type];

  return (
    <div
      style={{
        border: `2px solid ${colors.border}`,
        borderRadius: '8px',
        backgroundColor: colors.background,
        padding: '16px',
      }}
    >
      <InlineStack gap="400" blockAlign="center" wrap={false}>
        {/* Icon container - 40px square with colored background */}
        <Tooltip content={tooltipContent}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: colors.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'help',
            }}
          >
            <Icon source={icon} tone={colors.iconTone} />
          </div>
        </Tooltip>

        {/* Label and subtext with slider aligned to subtext row */}
        <Box width="100%">
          <BlockStack gap="050">
            <Text as="span" variant="bodyMd" fontWeight="semibold">
              {label}
            </Text>
            <InlineStack gap="300" blockAlign="center" wrap={false}>
              <div style={{ minWidth: '140px', flexShrink: 0 }}>
                <Text as="span" variant="bodySm" tone="subdued">
                  {daysLabel}
                </Text>
              </div>
              <Box width="100%">
                {slider || (
                  <div
                    style={{
                      height: '4px',
                      backgroundColor: colors.iconBg,
                      borderRadius: '2px',
                      opacity: 0.5,
                    }}
                  />
                )}
              </Box>
            </InlineStack>
          </BlockStack>
        </Box>
      </InlineStack>
    </div>
  );
}

export interface ThresholdStepProps {
  /** Selected products to display in the stack */
  selectedProducts: WizardProduct[];
  /** Callback to navigate back to product selection */
  onViewProducts: () => void;
  /** Optional callback when thresholds change (for parent state management) */
  onThresholdsChange?: (thresholds: {
    urgentThreshold: number;
    warningThreshold: number;
  }) => void;
}

export function ThresholdStep({ selectedProducts, onViewProducts, onThresholdsChange }: ThresholdStepProps) {
  // Velocity-based thresholds (days until stockout)
  const [urgentThreshold, setUrgentThreshold] = useState(3);
  const [warningThreshold, setWarningThreshold] = useState(7);

  // Validate threshold relationship and auto-adjust
  const handleUrgentChange = (value: number) => {
    if (value >= warningThreshold) {
      setWarningThreshold(value + 1);
    }
    setUrgentThreshold(value);
    onThresholdsChange?.({
      urgentThreshold: value,
      warningThreshold: value >= warningThreshold ? value + 1 : warningThreshold,
    });
  };

  const handleWarningChange = (value: number) => {
    if (value <= urgentThreshold) {
      setUrgentThreshold(value - 1);
    }
    setWarningThreshold(value);
    onThresholdsChange?.({
      urgentThreshold: value <= urgentThreshold ? value - 1 : urgentThreshold,
      warningThreshold: value,
    });
  };

  return (
    <BlockStack gap="500">
      {/* Header with selected products stack */}
      <InlineStack align="space-between" blockAlign="start">
        <InlineStack gap="200" blockAlign="center">
          <Icon source={AlertTriangleIcon} tone="subdued" />
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd" fontWeight="semibold">
              Configure alert thresholds
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Set when you want to be notified based on predicted days until stockout
            </Text>
          </BlockStack>
        </InlineStack>

        {/* Selected products stack - clickable to go back */}
        {selectedProducts.length > 0 && (
          <Tooltip content="Click to view and edit selected products">
            <button
              type="button"
              onClick={onViewProducts}
              className="threshold-products-stack"
              style={{
                position: 'relative',
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
            >
              {/* Count badge */}
              <div
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: selectedProducts.length === 1 ? '24px' : '12px',
                  minWidth: '22px',
                  height: '22px',
                  padding: '0 6px',
                  borderRadius: '11px',
                  backgroundColor: BRAND_PURPLE,
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                  border: '2px solid white',
                  zIndex: 10,
                }}
              >
                {selectedProducts.length}
              </div>

              {/* Stacked thumbnails - show up to 3 */}
              <div style={{ width: '72px', height: '56px', position: 'relative' }}>
                {selectedProducts.slice(-3).reverse().map((product, index, arr) => {
                  const stackPosition = arr.length - 1 - index;
                  return (
                    <div
                      key={product.id}
                      style={{
                        position: 'absolute',
                        top: stackPosition * 4,
                        left: stackPosition * 6,
                        zIndex: 3 - stackPosition,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: index === 0 ? '0 2px 6px rgba(0, 0, 0, 0.18)' : '0 1px 3px rgba(0, 0, 0, 0.12)',
                        border: '2px solid white',
                      }}
                    >
                      <Thumbnail
                        source={product.imageUrl || PackageIcon}
                        alt={product.title}
                        size="small"
                      />
                    </div>
                  );
                })}
              </div>
            </button>
          </Tooltip>
        )}
      </InlineStack>

      {/* Alert Threshold Rows */}
      <BlockStack gap="300">
        {/* Critical Row */}
        <ThresholdRow
          type="critical"
          icon={AlertTriangleIcon}
          label="Critical"
          tooltipContent={THRESHOLD_TOOLTIPS.critical}
          slider={
            <RangeSlider
              label="Critical threshold days"
              labelHidden
              value={urgentThreshold}
              min={1}
              max={14}
              step={1}
              output
              onChange={(value) => handleUrgentChange(value as number)}
            />
          }
          daysLabel={`0 - ${urgentThreshold} days until stockout`}
        />

        {/* Warning Row */}
        <ThresholdRow
          type="warning"
          icon={AlertCircleIcon}
          label="Warning"
          tooltipContent={THRESHOLD_TOOLTIPS.warning}
          slider={
            <RangeSlider
              label="Warning threshold days"
              labelHidden
              value={warningThreshold}
              min={2}
              max={30}
              step={1}
              output
              onChange={(value) => handleWarningChange(value as number)}
            />
          }
          daysLabel={`${urgentThreshold + 1} - ${warningThreshold} days until stockout`}
        />

        {/* Healthy Row - no slider, just display */}
        <ThresholdRow
          type="healthy"
          icon={CheckCircleIcon}
          label="Healthy"
          tooltipContent={THRESHOLD_TOOLTIPS.healthy}
          daysLabel={`${warningThreshold + 1}+ days until stockout`}
        />

        {/* Info note - compact grey style */}
        <Box background="bg-surface-secondary" paddingBlock="200" paddingInline="300" borderRadius="200">
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <Box>
              <Icon source={InfoIcon} tone="subdued" />
            </Box>
            <Text as="p" variant="bodySm" tone="subdued">
              These thresholds determine when products move between alert levels based on predicted
              days until stockout.
            </Text>
          </InlineStack>
        </Box>
      </BlockStack>
    </BlockStack>
  );
}
